import React, { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';

import { AnimatedViews } from '@garnish/core';

import { useA11ySelector } from '@sg/a11y-helpers';
import { type Allergen } from '@sg/graphql-schema';
import { getIconForAllergen, MenuDietaryPreferences } from '@sg/shared-fe-features-menu';
import {
  DietaryPreferencesContent,
  useDietaryPreferencesContent,
} from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { AccountScreenContainer, AccountScreenSection, AccountScreenTitle } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useDietaryPreferencesMachine } from '../../context/DietaryPreferences';
import { AccountDietaryRestrictionsLoading } from './components';

export const AccountDietaryRestrictionsScreen = (props: Props) => {
  const { headerShown } = props;

  const isLoggedIn = useIsLoggedIn();
  const containerA11ySelector = useA11ySelector('account.dietary-restrictions.container');

  // ─── Content ───────────────────────────────────────────────────────────────

  const content = useContentfulContentTypeEntry<DietaryPreferencesContent>({
    contentType: 'dietaryPreferences',
  });

  const { title, subtitle, disclaimer, confirmCta, icons } = useDietaryPreferencesContent(
    content?.data?.fields,
  );

  const isLoadingContent = content?.fetching;

  // ─── Machine ───────────────────────────────────────────────────────────────

  const {
    isIdle,
    sortedRestrictionsNames,
    pendingRestrictions,
    isUpdatingRemoteRestrictions,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    confirmChanges,
    toggleRestriction,
    cancelChanges,
    start,
  } = useDietaryPreferencesMachine({ referrer: 'AccountDietaryRestrictions' });

  // ─── Derived ─────────────────────────────────────────────────────────

  const isLoading = isLoadingContent || isIdle || isFetchingCustomerPreferences;

  // ─── Handlers ────────────────────────────────────────────────────────

  const startDietaryPreferencesMachineOnMount = useCallback(() => {
    if (!isLoggedIn) return;
    if (!isIdle) return;

    start({ stayActiveAfterConfirm: true });

    // cancel pending changes on screen blur
    return cancelChanges;
  }, [cancelChanges, isIdle, isLoggedIn, start]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(startDietaryPreferencesMachineOnMount);

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AccountScreenContainer
        headerShown={headerShown}
        screen="dietary-restrictions"
        {...containerA11ySelector.props}
      >
        <AccountScreenTitle title={title} />

        <AccountScreenSection>
          <AccountDietaryRestrictionsLoading />
        </AccountScreenSection>
      </AccountScreenContainer>
    );
  }

  return (
    <AccountScreenContainer
      headerShown={headerShown}
      screen="dietary-restrictions"
      {...containerA11ySelector.props}
    >
      <AccountScreenTitle title={title} />

      <AccountScreenSection>
        <AnimatedViews.Reveal>
          <MenuDietaryPreferences.Container withoutHorizontalSpacing>
            <MenuDietaryPreferences.Description>{subtitle}</MenuDietaryPreferences.Description>

            <MenuDietaryPreferences.ButtonsContainer>
              {sortedRestrictionsNames.map((restriction) => (
                <MenuDietaryPreferences.Button<Allergen>
                  icon={getIconForAllergen(restriction, icons)}
                  key={restriction}
                  restriction={restriction}
                  isLoading={isFetchingCustomerPreferences}
                  isChecked={pendingRestrictions[restriction]}
                  isDisabled={isUpdatingRemoteRestrictions}
                  toggleRestriction={toggleRestriction}
                />
              ))}
            </MenuDietaryPreferences.ButtonsContainer>

            <MenuDietaryPreferences.SubmitButton
              label={confirmCta}
              isLoading={isUpdatingRemoteRestrictions}
              isDisabled={!hasUnsavedPreferences}
              onPress={confirmChanges}
            />

            <MenuDietaryPreferences.Notices>{disclaimer}</MenuDietaryPreferences.Notices>
          </MenuDietaryPreferences.Container>
        </AnimatedViews.Reveal>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  headerShown?: boolean;
};
